'use client';

import type { RuleTarget } from '@inixiative/json-rules';
import {
  type Decoration,
  type GroupNode,
  type RuleBuilderSource,
  type SourceValues,
  useRuleBuilder,
} from '@inixiative/rules-builder';
import { Badge } from '@zealot/ui/components/badge';
import { Button } from '@zealot/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@zealot/ui/components/tooltip';
import { cn } from '@zealot/ui/lib/utils';
import { Plus } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { CombinatorSelect } from './controls';
import { BuilderContext, facetGroups, MutateContext, orderDecoratedFields, type RuleBuilderValue } from './lib';
import { Node } from './rows';

export type { RuleBuilderValue } from './lib';

// gloss
export type RuleBuilderProps = {
  source: RuleBuilderSource;
  sourceValues?: SourceValues[];
  defaultValue?: RuleBuilderValue;
  onChange?: (value: RuleBuilderValue) => void;
  maxDepth?: number;
  labels?: Record<string, string>;
  valueLabels?: Record<string, Record<string, string>>;
  operatorLabels?: Record<string, string>;
  decoration?: Decoration;
  narrowing?: ReactNode;
  sentence?: { before: string; after: string; showLeadInWhenEmpty?: boolean };
  collapsible?: boolean;
  targets?: RuleTarget[];
  disabled?: boolean;
  className?: string;
};

// gloss
export const SentenceRoot = ({
  root,
  sentence,
}: {
  root: GroupNode;
  sentence: { before: string; after: string; showLeadInWhenEmpty?: boolean };
}) => {
  const children = root.children;
  const hasRows = children.length > 0;
  return (
    <div data-slot="rule-sentence-root" className="flex flex-col gap-3">
      {}
      {hasRows || (sentence.showLeadInWhenEmpty ?? true) ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {sentence.before}
          <CombinatorSelect operator={root.operator} />
          {sentence.after}
        </div>
      ) : null}
      {hasRows ? (
        <div className="flex flex-col gap-3">
          {children.map((child) => (
            <Node key={child.id} node={child} />
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                onClick={root.addRule}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg border-2 border-border border-dashed bg-muted/30 text-muted-foreground text-sm transition-colors hover:bg-muted/60 hover:text-foreground"
              >
                <Plus className="size-4" />
                {hasRows ? 'Add another condition' : 'Add a condition'}
              </button>
            }
          />
          <TooltipContent>Adds one more condition to this rule</TooltipContent>
        </Tooltip>
        {}
        {root.canAddGroup ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button type="button" variant="ghost" className="h-12" onClick={root.addGroup}>
                  {root.operator.value === 'any' ? 'Add "and" group' : 'Add "either/or" group'}
                </Button>
              }
            />
            <TooltipContent>
              {root.operator.value === 'any'
                ? 'For mixed logic like "A or (B and C)" — starts a nested group where every condition must match'
                : 'For mixed logic like "A and (B or C)" — starts a nested group; set it to "any" so matching one of its conditions is enough'}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

// gloss
export const RuleBuilder = ({
  source,
  sourceValues,
  defaultValue,
  onChange,
  maxDepth,
  labels,
  valueLabels,
  operatorLabels,
  decoration,
  narrowing,
  sentence,
  collapsible,
  targets,
  disabled = false,
  className,
}: RuleBuilderProps) => {
  const isSentence = Boolean(sentence);

  const seedValue = useMemo(() => {
    if (!isSentence || !defaultValue) return defaultValue;
    const tree = defaultValue as Record<string, unknown>;
    return Array.isArray(tree.all) || Array.isArray(tree.any)
      ? defaultValue
      : ({ all: [defaultValue] } as RuleBuilderValue);
  }, [isSentence, defaultValue]);

  const orderedSource = useMemo(() => orderDecoratedFields(source, decoration), [source, decoration]);

  const { root, value, setCondition } = useRuleBuilder({
    source: orderedSource,
    sourceValues,
    maxDepth,
    labels,
    valueLabels,
    decoration,
    targets,
    defaultValue: seedValue,
    onChange,
  });

  const builderContext = useMemo(() => {
    const rootTitle = (decoration as { rootContainer?: { label?: string } } | undefined)?.rootContainer?.label;
    const rootLabel = rootTitle ?? decoration?.labels?.models?.[source.model]?.label ?? source.model;
    return {
      groups: facetGroups(decoration, rootLabel),
      rootLabel,
      rootIcon: decoration?.labels?.models?.[source.model]?.icon,
      sentence: isSentence,
      sourceValues,
      source,
      operatorLabels,
      decoration,
      collapsible,
    };
  }, [decoration, source, isSentence, sourceValues, operatorLabels, collapsible]);
  const mutate = useMemo(() => ({ value, setCondition }), [value, setCondition]);

  return (
    <BuilderContext.Provider value={builderContext}>
      <MutateContext.Provider value={mutate}>
        <div
          data-slot="rule-builder"
          inert={disabled}
          aria-disabled={disabled || undefined}
          className={cn('space-y-3', disabled && 'opacity-60', className)}
        >
          {narrowing ? (
            <Badge variant="outline" data-slot="rule-narrowing" className="gap-1.5 font-normal">
              <span className="text-muted-foreground">Authoring within:</span>
              <span>{narrowing}</span>
            </Badge>
          ) : null}
          {sentence && root.kind === 'group' ? <SentenceRoot root={root} sentence={sentence} /> : <Node node={root} />}
        </div>
      </MutateContext.Provider>
    </BuilderContext.Provider>
  );
};
